import type { HTMLAttributes, KeyboardEvent, ReactNode } from 'react'
import { cx } from './cx'
import { Icon } from './Icon'
import { HelpTip, isExplanation } from './HelpTip'
import { AnimatedValue } from './AnimatedValue'

export interface CardProps extends HTMLAttributes<HTMLElement> {
  variant?: 'elevated' | 'filled' | 'outlined' | undefined
  /** none = i figli gestiscono il proprio padding (es. tabella a tutta larghezza). */
  padding?: 'none' | 'sm' | 'md' | undefined
  /** `button` (o un `onClick` su section/div) rende la card interattiva: stati, elevazione e tastiera M3. */
  as?: 'section' | 'div' | 'button' | undefined
}

export function Card({ variant = 'outlined', padding = 'md', as: Tag = 'section', className, onClick, onKeyDown, ...rest }: CardProps) {
  const interactive = Tag === 'button' || Boolean(onClick)
  const cls = cx('md-card', `md-card--${variant}`, `md-card--pad-${padding}`, interactive && 'md-card--interactive md-state', className)
  if (Tag === 'button') {
    return <button type="button" {...(rest as HTMLAttributes<HTMLButtonElement>)} onClick={onClick} onKeyDown={onKeyDown} className={cls} />
  }
  if (onClick) {
    // Card azionabile non-button: role button, focalizzabile, Invio/Spazio come il clic.
    const key = (e: KeyboardEvent<HTMLElement>) => {
      onKeyDown?.(e)
      if (e.defaultPrevented || e.target !== e.currentTarget) return
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        e.currentTarget.click()
      }
    }
    return <Tag role="button" tabIndex={0} {...rest} onClick={onClick} onKeyDown={key} className={cls} />
  }
  return <Tag {...rest} onKeyDown={onKeyDown} className={cls} />
}

export interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title: ReactNode
  subtitle?: ReactNode | undefined
  icon?: string | undefined
  actions?: ReactNode | undefined
  /** Livello del titolo (default 2). */
  headingLevel?: 2 | 3 | 4 | undefined
}

export function CardHeader({ title, subtitle, icon, actions, headingLevel = 2, className, ...rest }: CardHeaderProps) {
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4'
  return (
    <div {...rest} className={cx('md-card__header', className)}>
      {icon ? <Icon name={icon} size={24} className="md-card__header-icon" /> : null}
      <div className="md-card__header-text">
        <Heading className="md-card__title">{title}{isExplanation(subtitle) ? <HelpTip>{subtitle}</HelpTip> : null}</Heading>
        {subtitle && !isExplanation(subtitle) ? <p className="md-card__subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="md-card__header-actions">{actions}</div> : null}
    </div>
  )
}

export function CardContent({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div {...rest} className={cx('md-card__content', className)} />
}

export interface CardActionsProps extends HTMLAttributes<HTMLDivElement> {
  align?: 'start' | 'end' | undefined
}

export function CardActions({ align = 'end', className, ...rest }: CardActionsProps) {
  return <div {...rest} className={cx('md-card__actions', align === 'start' && 'md-card__actions--start', className)} />
}

export interface StatCardProps extends Omit<HTMLAttributes<HTMLElement>, 'onClick'> {
  label: string
  value: ReactNode
  delta?: { value: string; trend: 'up' | 'down' | 'flat' } | undefined
  icon?: string | undefined
  /**
   * Con `icon` il tono colora l'icona (primary di default). Senza icona, success/warning/error colorano il
   * valore con i ruoli M3 (success/warning/error); primary e neutral lasciano il valore in on-surface.
   */
  tone?: 'primary' | 'neutral' | 'success' | 'warning' | 'error' | undefined
  /** Riga di contesto sotto il valore (periodo, obiettivo, nota): body-small on-surface-variant. */
  supporting?: ReactNode | undefined
  /** Se presente la card e' un pulsante. */
  onClick?: (() => void) | undefined
}

const TREND_ICON = { up: 'trending_up', down: 'trending_down', flat: 'trending_flat' } as const
const TREND_LABEL = { up: 'in aumento', down: 'in calo', flat: 'stabile' } as const

/** Indicatore numerico (KPI): etichetta, valore, variazione e riga di contesto (`supporting`). */
export function StatCard({ label, value, delta, icon, tone = 'primary', supporting, onClick, className, ...rest }: StatCardProps) {
  const valueTone = !icon && (tone === 'success' || tone === 'warning' || tone === 'error') ? `md-stat__value--${tone}` : undefined
  const body = (
    <>
      <div className="md-stat__top">
        <span className="md-stat__label">{label}</span>
        {icon ? (
          <span className={cx('md-stat__icon', `md-tone--${tone === 'neutral' ? 'primary' : tone}`)}>
            <Icon name={icon} size={20} />
          </span>
        ) : null}
      </div>
      <div className={cx('md-stat__value', 'md-num', valueTone)}><AnimatedValue value={value} /></div>
      {delta ? (
        <div className={cx('md-stat__delta', `md-stat__delta--${delta.trend}`)}>
          <Icon name={TREND_ICON[delta.trend]} size={18} />
          <span>
            {delta.value}
            <span className="md-sr-only"> ({TREND_LABEL[delta.trend]})</span>
          </span>
        </div>
      ) : null}
      {supporting ? <div className="md-stat__supporting">{supporting}</div> : null}
    </>
  )
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cx('md-card', 'md-card--filled', 'md-card--interactive', 'md-stat', 'md-stat--action', 'md-state', className)} {...(rest as HTMLAttributes<HTMLButtonElement>)}>
        {body}
      </button>
    )
  }
  return (
    <div {...rest} className={cx('md-card', 'md-card--filled', 'md-stat', className)}>
      {body}
    </div>
  )
}
